import { Injectable } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
  providedIn: 'root',
})
export class DateUtilityService {
  /**
   * Get current date in ISO format (YYYY-MM-DD)
   */
  getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  /**
   * Convert NgbDateStruct to Date object with time set to midnight
   */
  ngbDateStructToDate(ngbDate: NgbDateStruct) {
    const date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Get Date object with time set to midnight
   */
  getDateAtMidnight(date: Date) {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  }

  /**
   * Get today as a Date object with time set to midnight
   */
  getTodayDateObject() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  /**
   * Convert any date format to YYYY-MM-DD format for API calls
   */
  ensureISODateFormat(dateString: string) {
    if (dateString && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    const parsedDate = new Date(dateString);
    if (!isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return this.getTodayDate();
  }

  /**
   * Parse ISO date string (YYYY-MM-DD) to NgbDateStruct
   */
  parseISODate(dateString: string): NgbDateStruct | null {
    if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return null;
    }
    const [year, month, day] = dateString.split('-').map(Number);
    return { year, month, day };
  }

  /**
   * Format ISO date (YYYY-MM-DD) to MM/DD/YYYY
   */
  formatDateToMM_DD_YYYY(dateString: string) {
    if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return '';
    }
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  }

  /**
   * Format NgbDateStruct to MM/DD/YYYY
   */
  formatNgbDateToMM_DD_YYYY(ngbDate: NgbDateStruct) {
    const month = String(ngbDate.month).padStart(2, '0');
    const day = String(ngbDate.day).padStart(2, '0');
    return `${month}/${day}/${ngbDate.year}`;
  }

  /**
   * Parse MM/DD/YYYY to NgbDateStruct
   */
  parseMMDDYYYY(dateString: string): NgbDateStruct | null {
    if (!dateString || dateString.length !== 10) {
      return null;
    }
    const [month, day, year] = dateString.split('/').map(Number);
    if (
      !month ||
      !day ||
      !year ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }
    return { year, month, day };
  }

  /**
   * Convert MM/DD/YYYY to ISO format (YYYY-MM-DD)
   */
  convertMMDDYYYYtoISO(dateString: string) {
    if (!dateString || dateString.length !== 10) {
      return '';
    }
    const [month, day, year] = dateString.split('/');
    const paddedMonth = month.padStart(2, '0');
    const paddedDay = day.padStart(2, '0');
    return `${year}-${paddedMonth}-${paddedDay}`;
  }

  /**
   * Format time from API format "07:30am" to display format "7:30 am"
   */
  formatTime(time: string) {
    if (!time) return '';
    // Add space before am/pm and remove leading zero
    const withSpace = time.replace(/(am|pm)$/i, ' $1');
    return withSpace.startsWith('0') ? withSpace.slice(1) : withSpace;
  }

  /**
   * Format days from API format "Mon,Tue,Wed,Thu,Fri" to display format "Mon – Fri"
   */
  formatDays(days: string) {
    if (!days) return '';

    if (days === 'Mon,Tue,Wed,Thu,Fri') {
      return 'Mon - Fri';
    } else if (days === 'Sat') {
      return 'Sat';
    } else if (days === 'Sat,Sun') {
      return 'Sat, Sun';
    } else if (days.includes(',')) {
      const daysList = days.split(',');
      // Check if consecutive
      const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const indices = daysList
        .map((d) => dayOrder.indexOf(d))
        .filter((i) => i >= 0);
      const isConsecutive =
        indices.length > 1 &&
        indices.every((val, i, arr) => i === 0 || val === arr[i - 1] + 1);

      if (isConsecutive && daysList.length > 2) {
        return `${daysList[0]} – ${daysList[daysList.length - 1]}`;
      }
      return daysList.join(', ');
    }
    return days;
  }
}
